import { useQuery } from "@apollo/client/react"
import { ArrowRight, Map, ScanLine } from "lucide-react"
import { FormEvent, lazy, Suspense, useState } from "react"
import { Link, Navigate, useNavigate } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { cn } from "~/common/cn"
import { dashboardPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import wordmarkUrl from "~/images/wp-mascot-wordmark.png"
import { Button } from "~/ui/button"

const RecentStopsMap = lazy(() =>
  import("~/maps/recent-stops-map").then((m) => ({ default: m.RecentStopsMap }))
)

const inputClassName =
  "flex h-12 w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"

const TRACK_HERO_IMAGE =
  "https://images.pexels.com/photos/1586950/pexels-photo-1586950.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
const REGISTER_HERO_IMAGE =
  "https://images.pexels.com/photos/592753/pexels-photo-592753.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"

const siteStatsQuery = gql(/* GraphQL */ `
  query LandingStats {
    siteStats {
      puzzlesRegisteredCount
      journeyLogsCount
      featuredPuzzles {
        id
        wpId
        title
        brand
        pieceCount
        photoThumbUrl
      }
      recentMapStops(first: 50) {
        id
        city
        state
        completedOn
        authorDisplayName
        latitude
        longitude
        photoThumbUrl
        puzzle {
          wpId
          title
        }
      }
    }
  }
`)

const STEPS = [
  {
    title: "Find a wandering puzzle",
    body: "Stickers travel on puzzle boxes. Scan the QR with any phone camera.",
  },
  {
    title: "Complete it",
    body: "Take your time. Borrow help. Make a weekend of it.",
  },
  {
    title: "Log your journey",
    body: "A few words about how it went, where you finished it, and a photo if you like.",
  },
  {
    title: "Pass it on",
    body: "Hand it to a friend, leave it at a coffee shop, or mail it across the country.",
  },
]

export const LandingScreen = () => {
  useDocumentTitle("Wandering Puzzles", "")
  const navigate = useNavigate()
  const { viewer } = useViewerMaybe()
  const [trackInput, setTrackInput] = useState("")
  const { data } = useQuery(siteStatsQuery)

  if (viewer) {
    return <Navigate to={dashboardPath({})} replace />
  }
  const stats = data?.siteStats
  const showStats = stats && stats.puzzlesRegisteredCount + stats.journeyLogsCount >= 10
  const featured = stats?.featuredPuzzles ?? []
  const mapStops = stats?.recentMapStops ?? []

  const handleTrack = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = trackInput.trim()
    if (!trimmed) return
    navigate(`/p/${trimmed}`)
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-4xl flex-col px-5 py-8 sm:px-6 sm:py-16">
      <header className="text-center">
        <img src={wordmarkUrl} alt="Wandering Puzzles" className="mx-auto h-44 w-auto sm:h-56" />
        <h1 className="mt-2 text-[2.5rem] font-semibold leading-[1.05] tracking-tight sm:mt-4 sm:text-6xl">
          Track puzzles as they wander the world.
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-700 sm:text-xl">
          A free community for following the journey of jigsaw puzzles, one finder at a time.
        </p>
      </header>

      <section className="mt-10 grid gap-5 sm:mt-12 sm:grid-cols-2 sm:gap-6">
        <form
          onSubmit={handleTrack}
          className="relative overflow-hidden rounded-2xl border border-amber-200 shadow-sm"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(255,251,235,0.78) 0%, rgba(255,251,235,0.92) 70%, rgba(255,251,235,0.98) 100%), url(${TRACK_HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-2 text-amber-800">
              <ScanLine size={20} aria-hidden />
              <span className="text-sm font-semibold uppercase tracking-wider">Track</span>
            </div>
            <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">Track a puzzle</h2>
            <p className="mt-2 text-base text-gray-700 sm:text-lg">
              Enter the WP ID printed on the sticker.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                placeholder="WP-1234-AA"
                value={trackInput}
                onChange={(e) => setTrackInput(e.target.value)}
                aria-label="Puzzle ID"
                className={cn(inputClassName, "font-mono text-base sm:text-lg")}
              />
              <Button
                type="submit"
                disabled={!trackInput.trim()}
                className="h-12 px-5 text-base sm:shrink-0"
              >
                Look up
              </Button>
            </div>
          </div>
        </form>

        <div
          className="relative flex flex-col overflow-hidden rounded-2xl border border-stone-700 text-white shadow-sm"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(28,25,23,0.62) 0%, rgba(28,25,23,0.86) 100%), url(${REGISTER_HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="flex flex-1 flex-col p-6 sm:p-7">
            <div className="flex items-center gap-2 text-amber-200">
              <Map size={20} aria-hidden />
              <span className="text-sm font-semibold uppercase tracking-wider">Register</span>
            </div>
            <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">Register a new puzzle</h2>
            <p className="mt-2 text-base text-stone-200 sm:text-lg">
              Add a puzzle from your shelf and download a printable QR sticker.
            </p>
            <div className="mt-5 sm:mt-auto sm:pt-4">
              <Link
                to="/puzzles/new"
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-white px-5 text-base font-medium text-stone-900 transition hover:bg-stone-100 sm:w-auto"
              >
                Get started
                <ArrowRight size={18} aria-hidden />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {mapStops.length > 0 ? (
        <section className="mt-14">
          <h2 className="text-center text-2xl font-semibold sm:text-3xl">
            Where they&apos;re going
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-base text-gray-700 sm:text-lg">
            Recent stops from finders all over. Click a pin to follow that puzzle&apos;s journey.
          </p>
          <Suspense
            fallback={<div className="mt-6 h-72 animate-pulse rounded-2xl bg-stone-100 sm:h-96" />}
          >
            <RecentStopsMap stops={mapStops} className="mt-6 h-72 sm:h-96" />
          </Suspense>
        </section>
      ) : null}

      {featured.length > 0 ? (
        <section className="mt-14">
          <h2 className="text-center text-2xl font-semibold sm:text-3xl">Currently Wandering</h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-base text-gray-700 sm:text-lg">
            Puzzles that have recently logged a step on their journey!
          </p>
          <ul className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/p/${p.wpId}`}
                  className="flex h-full gap-4 rounded-xl border border-stone-200 bg-white p-4 shadow-sm transition hover:border-stone-400 hover:shadow"
                >
                  {p.photoThumbUrl ? (
                    <img
                      src={p.photoThumbUrl}
                      alt={p.title}
                      className="h-24 w-24 shrink-0 rounded-lg bg-stone-100 object-contain"
                    />
                  ) : (
                    <div className="h-24 w-24 shrink-0 rounded-lg bg-stone-100" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-lg font-semibold">{p.title}</div>
                    <div className="mt-1 text-sm text-gray-700">
                      {p.brand} · {p.pieceCount.toLocaleString()} pieces
                    </div>
                    <div className="mt-2 font-mono text-xs text-gray-500">{p.wpId}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mt-14">
        <h2 className="text-center text-2xl font-semibold sm:text-3xl">How it works</h2>
        <ol className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, idx) => (
            <li
              key={step.title}
              className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-6"
            >
              <div className="font-display text-3xl leading-none text-amber-700">{idx + 1}</div>
              <h3 className="mt-3 text-lg font-semibold sm:text-xl">{step.title}</h3>
              <p className="mt-2 text-base leading-relaxed text-gray-700">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {showStats ? (
        <section className="mt-14 rounded-2xl border border-stone-200 bg-white p-7 text-center shadow-sm sm:p-8">
          <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.18em] text-amber-700 sm:text-base">
            Wandering so far
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:gap-10">
            <div>
              <div className="font-display text-5xl font-semibold sm:text-6xl">
                {stats.puzzlesRegisteredCount.toLocaleString()}
              </div>
              <div className="mt-1 text-base text-gray-700">puzzles</div>
            </div>
            <div>
              <div className="font-display text-5xl font-semibold sm:text-6xl">
                {stats.journeyLogsCount.toLocaleString()}
              </div>
              <div className="mt-1 text-base text-gray-700">journey logs</div>
            </div>
          </div>
        </section>
      ) : null}

      <footer className="mt-auto pt-16 text-center text-base text-gray-600">
        <nav className="space-x-5">
          <Link to="/about" className="hover:text-gray-900">
            About
          </Link>
          <Link to="/faq" className="hover:text-gray-900">
            FAQ
          </Link>
          <Link to="/contact" className="hover:text-gray-900">
            Contact
          </Link>
        </nav>
      </footer>
    </main>
  )
}
